import React, { ReactNode } from 'react';
import ReactModal from 'react-modal';

import { ButtonSizes } from '@rover/kibble/core/Button/Button.common';
import { Props as FlexProps } from '@rover/kibble/core/Flex/Flex';
import BottomSheet from '@rover/kibble/official-patterns/BottomSheet';
import Modal from '@rover/kibble/official-patterns/Modal';
import MQ from '@rover/kibble/styles/MediaQuery';
import useMatchMedia from '@rover/react-lib/src/hooks/useMatchMedia';

export type MobileProps = {
  isFullHeight?: boolean;
  style?: ReactModal.Styles;
  showCloseIcon?: boolean;
  closeOnDragDown?: boolean;
  headerId?: string;
  HeaderComponent?: ReactNode;
  animationDirection?: React.ComponentProps<typeof BottomSheet>['animationDirection'];
};

export type DesktopProps = {
  overlaySx?: React.CSSProperties;
  showCloseIcon?: boolean;
  closeIconSize?: ButtonSizes;
  contentProps?: FlexProps;
  headerText?: string;
  headerId?: string;
  headerSize?: React.ComponentProps<typeof Modal>['headerSize'];
  headerProps?: FlexProps;
};

export const DESKTOP_CONTENT_PROPS: FlexProps = {
  width: '600px',
  maxHeight: 'calc(100vh - 200px)',
  sx: { overflowY: 'auto' },
};

export type DynamicModalProps = {
  children: ReactNode;
  isOpen: boolean;
  onRequestClose: () => void;
  mobileProps?: MobileProps;
  desktopProps?: DesktopProps;
};

const DynamicKibbleModal = ({
  children,
  isOpen,
  onRequestClose,
  mobileProps = {},
  desktopProps = {},
}: DynamicModalProps): JSX.Element => {
  const isMobile = useMatchMedia(MQ.XS_DOWN.getBreakpointString());

  if (isMobile) {
    return (
      <BottomSheet
        isOpen={isOpen}
        onRequestClose={onRequestClose}
        isFullHeight={mobileProps.isFullHeight}
        style={mobileProps.style}
        headerId={mobileProps.headerId}
        HeaderComponent={mobileProps.HeaderComponent}
        hideCloseIcon={!mobileProps.showCloseIcon}
        closeOnDragDown={mobileProps.closeOnDragDown}
        animationDirection={mobileProps.animationDirection}
      >
        {children}
      </BottomSheet>
    );
  }
  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      overlaySx={desktopProps.overlaySx || undefined}
      showCloseIcon={desktopProps.showCloseIcon}
      closeIconSize={desktopProps.closeIconSize}
      contentProps={{ ...DESKTOP_CONTENT_PROPS, ...desktopProps.contentProps }}
      headerText={desktopProps.headerText}
      headerId={desktopProps.headerId}
      headerSize={desktopProps.headerSize}
      headerProps={desktopProps.headerProps}
    >
      {children}
    </Modal>
  );
};

export default DynamicKibbleModal;
